export type GeolocationPermissionState = PermissionState | "unsupported" | "unavailable";
export type GeolocationAccessStatus = "granted" | "prompt" | "denied" | "unavailable";

export type GeolocationAccessResolution = {
    permissionState: GeolocationPermissionState;
    accessStatus: GeolocationAccessStatus;
    position?: GeolocationPosition;
    error?: GeolocationPositionError | Error;
};

type PermissionAwareNavigator = Navigator & {
    permissions?: {
        query: (descriptor: { name: string }) => Promise<PermissionStatus>;
    };
};

const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "::1"]);
const GEOLOCATION_SESSION_ACCESS_KEY = "marineone.geolocation.session-access";

const geoTrace = (message: string, details?: unknown) => {
    if (details === undefined) {
        console.info(`[GeoTrace] ${message}`);
        return;
    }

    console.info(`[GeoTrace] ${message}`, details);
};

const hasSessionGeolocationAccess = (): boolean => {
    if (typeof window === "undefined") {
        return false;
    }

    try {
        return window.sessionStorage.getItem(GEOLOCATION_SESSION_ACCESS_KEY) === "granted";
    } catch {
        return false;
    }
};

const setSessionGeolocationAccess = (isGranted: boolean) => {
    if (typeof window === "undefined") {
        return;
    }

    try {
        if (isGranted) {
            window.sessionStorage.setItem(GEOLOCATION_SESSION_ACCESS_KEY, "granted");
            return;
        }

        window.sessionStorage.removeItem(GEOLOCATION_SESSION_ACCESS_KEY);
    } catch {
        return;
    }
};

export const MANUAL_GEOLOCATION_OPTIONS: PositionOptions = {
    enableHighAccuracy: true,
    timeout: 15000,
    maximumAge: 0,
};

export const TRACKING_GEOLOCATION_OPTIONS: PositionOptions = {
    enableHighAccuracy: true,
    timeout: 20000,
    maximumAge: 5000,
};

export const isGeolocationSupported = (): boolean => {
    return typeof window !== "undefined" && typeof navigator !== "undefined" && "geolocation" in navigator;
};

export const isGeolocationSecureContext = (): boolean => {
    if (typeof window === "undefined") {
        return false;
    }

    if (window.isSecureContext) {
        return true;
    }

    return LOCAL_HOSTNAMES.has(window.location.hostname);
};

export const canUseGeolocation = (): boolean => {
    return isGeolocationSupported() && isGeolocationSecureContext();
};

export const getGeolocationUnavailableMessage = (): string => {
    if (!isGeolocationSupported()) {
        return "Geolocation is not supported by this browser.";
    }

    if (!isGeolocationSecureContext()) {
        return "Geolocation requires a secure context (HTTPS or localhost).";
    }

    return "Geolocation is unavailable.";
};

export const getGeolocationPermissionState = async (): Promise<GeolocationPermissionState> => {
    if (!canUseGeolocation()) {
        setSessionGeolocationAccess(false);
        geoTrace("permission state unavailable because geolocation cannot be used", {
            supported: isGeolocationSupported(),
            secureContext: isGeolocationSecureContext(),
        });
        return "unavailable";
    }

    const permissionsApi = (navigator as PermissionAwareNavigator).permissions;
    if (!permissionsApi?.query) {
        const fallbackState = hasSessionGeolocationAccess() ? "granted" : "unsupported";
        geoTrace("permissions api unavailable, using fallback permission state", {
            sessionAccess: hasSessionGeolocationAccess(),
            resolvedState: fallbackState,
        });
        return fallbackState;
    }

    try {
        const permissionStatus = await permissionsApi.query({ name: "geolocation" });
        geoTrace("permissions api returned geolocation state", {
            state: permissionStatus.state,
            sessionAccess: hasSessionGeolocationAccess(),
        });

        if (permissionStatus.state === "denied") {
            setSessionGeolocationAccess(false);
            geoTrace("permission denied, cleared session access flag");
            return permissionStatus.state;
        }

        if (permissionStatus.state === "granted") {
            setSessionGeolocationAccess(true);
            geoTrace("permission granted, stored session access flag");
            return permissionStatus.state;
        }

        geoTrace("permission state is prompt or transient, returning browser state", {
            state: permissionStatus.state,
        });
        return permissionStatus.state;
    } catch {
        const fallbackState = hasSessionGeolocationAccess() ? "granted" : "unsupported";
        geoTrace("permissions api query failed, using fallback permission state", {
            sessionAccess: hasSessionGeolocationAccess(),
            resolvedState: fallbackState,
        });
        return fallbackState;
    }
};

export const requestCurrentPosition = (
    options: PositionOptions = MANUAL_GEOLOCATION_OPTIONS
): Promise<GeolocationPosition> => {
    return new Promise((resolve, reject) => {
        if (!canUseGeolocation()) {
            setSessionGeolocationAccess(false);
            geoTrace("current position request aborted because geolocation cannot be used", {
                supported: isGeolocationSupported(),
                secureContext: isGeolocationSecureContext(),
            });
            reject(new Error(getGeolocationUnavailableMessage()));
            return;
        }

        geoTrace("requesting current position", {
            options,
            sessionAccess: hasSessionGeolocationAccess(),
        });

        navigator.geolocation.getCurrentPosition(
            (position) => {
                setSessionGeolocationAccess(true);
                geoTrace("current position request succeeded", {
                    accuracy: position.coords.accuracy,
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    timestamp: position.timestamp,
                });
                resolve(position);
            },
            (error) => {
                if (error.code === 1) {
                    setSessionGeolocationAccess(false);
                }

                geoTrace("current position request failed", {
                    code: error.code,
                    message: error.message,
                    sessionAccess: hasSessionGeolocationAccess(),
                });

                reject(error);
            },
            options
        );
    });
};

export const startWatchingPosition = (
    onSuccess: PositionCallback,
    onError?: PositionErrorCallback,
    options: PositionOptions = TRACKING_GEOLOCATION_OPTIONS
): number | null => {
    if (!canUseGeolocation()) {
        setSessionGeolocationAccess(false);
        geoTrace("watch position aborted because geolocation cannot be used", {
            supported: isGeolocationSupported(),
            secureContext: isGeolocationSecureContext(),
        });
        return null;
    }

    geoTrace("starting watch position", {
        options,
        sessionAccess: hasSessionGeolocationAccess(),
    });

    return navigator.geolocation.watchPosition(
        (position) => {
            setSessionGeolocationAccess(true);
            geoTrace("watch position update received", {
                accuracy: position.coords.accuracy,
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                timestamp: position.timestamp,
            });
            onSuccess(position);
        },
        (error) => {
            if (error.code === 1) {
                setSessionGeolocationAccess(false);
            }

            geoTrace("watch position failed", {
                code: error.code,
                message: error.message,
                sessionAccess: hasSessionGeolocationAccess(),
            });

            onError?.(error);
        },
        options
    );
};

export const stopWatchingPosition = (watchId: number | null | undefined) => {
    if (watchId === null || watchId === undefined || !isGeolocationSupported()) {
        return;
    }

    geoTrace("stopping watch position", { watchId });
    navigator.geolocation.clearWatch(watchId);
};

export const isGeolocationPermissionDenied = (
    error?: Pick<GeolocationPositionError, "code"> | null
): boolean => {
    return error?.code === 1;
};

export const getGeolocationErrorMessage = (
    error?: Pick<GeolocationPositionError, "code"> | null
): string => {
    if (!error) {
        return "An unknown error occurred.";
    }

    if (error.code === 1) {
        return "User denied the request for geolocation.";
    }

    if (error.code === 2) {
        return "Location information is unavailable.";
    }

    if (error.code === 3) {
        return "The request to get user location timed out.";
    }

    return "An unknown error occurred.";
};

export const hasEffectiveSessionGeolocationAccess = (): boolean => {
    return hasSessionGeolocationAccess();
};

export const resolveGeolocationAccess = async (
    options: PositionOptions = MANUAL_GEOLOCATION_OPTIONS
): Promise<GeolocationAccessResolution> => {
    const permissionState = await getGeolocationPermissionState();
    const sessionAccess = hasSessionGeolocationAccess();

    geoTrace("resolving effective geolocation access", {
        permissionState,
        sessionAccess,
        options,
    });

    if (permissionState === "denied") {
        return { permissionState, accessStatus: "denied" };
    }

    if (permissionState === "unavailable") {
        return { permissionState, accessStatus: "unavailable" };
    }

    if (permissionState === "granted") {
        return { permissionState, accessStatus: "granted" };
    }

    if (!sessionAccess) {
        return { permissionState, accessStatus: "prompt" };
    }

    try {
        const position = await requestCurrentPosition(options);
        geoTrace("effective geolocation access verified from session access", {
            permissionState,
        });
        return {
            permissionState,
            accessStatus: "granted",
            position,
        };
    } catch (error) {
        const geolocationError = error as GeolocationPositionError | Error;

        if (isGeolocationPermissionDenied(geolocationError as GeolocationPositionError)) {
            geoTrace("effective geolocation access verification failed with permission denial", {
                permissionState,
            });
            return {
                permissionState,
                accessStatus: "denied",
                error: geolocationError,
            };
        }

        geoTrace("effective geolocation access verification failed without permission denial", {
            permissionState,
            error: geolocationError,
        });
        return {
            permissionState,
            accessStatus: "granted",
            error: geolocationError,
        };
    }
};